import type * as Sentry from '@sentry/nextjs';

import { type Logger } from './model.js';

/**
 * Map Sentry log functions onto their console counterparts
 * so that both are called when the logger is used. Only send
 * to Sentry if the severity level is high enough.
 */
const buildSentryLogFns = ({
  logger,
  sentry,
  maxSeverity = 'warning',
}: {
  logger: Logger;
  sentry: typeof Sentry;
  maxSeverity: Sentry.SeverityLevel;
}) => {
  const severities = ['fatal', 'error', 'warning', 'log', 'info', 'debug'];
  const maxSeverityIndex = severities.indexOf(maxSeverity);
  const neededSeverities = severities.slice(0, maxSeverityIndex + 1);

  if (maxSeverityIndex === -1) {
    throw new Error(
      `Invalid Sentry severity provided! Received: ${maxSeverity}. Allowed: ${severities.join(
        ', ',
      )}`,
    );
  }

  if (
    neededSeverities.includes('fatal') ||
    neededSeverities.includes('error')
  ) {
    const originalError = logger.error;
    logger.error = (...args) => {
      originalError(...args);
      sentry.captureException(...args);
    };
  }

  if (neededSeverities.includes('warning')) {
    const originalWarn = logger.warn;
    logger.warn = (...args) => {
      originalWarn(...args);
      sentry.captureMessage(...args, 'warning');
    };
  }

  if (neededSeverities.includes('log')) {
    const originalLog = logger.log;
    logger.log = (...args) => {
      originalLog(...args);
      sentry.captureMessage(...args, 'log');
    };
  }

  if (neededSeverities.includes('info')) {
    const originalInfo = logger.info;
    logger.info = (...args) => {
      originalInfo(...args);
      sentry.captureMessage(...args, 'info');
    };
  }

  if (neededSeverities.includes('debug')) {
    const originalDebug = logger.debug;
    logger.debug = (...args) => {
      originalDebug(...args);
      sentry.captureMessage(...args, 'debug');
    };
  }
};

/**
 * Initialize Sentry by proxying logger calls and storing the root Sentry
 * object on the logger itself as an escape hatch.
 */
export const initSentry = async (logger: Logger) => {
  const sentry = await import('@sentry/nextjs');
  logger.sentry = sentry;
  buildSentryLogFns({
    logger,
    sentry,
    maxSeverity: process.env
      .NEXT_PUBLIC_SENTRY_SEVERITY as Sentry.SeverityLevel,
  });
};
